import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Language, Post } from 'src/app/core/models/core.models';
import {
  createSynopsis,
  deleteSynopsis,
  translatePost,
  updatePostTranslation,
  updateSynopsis,
} from 'src/app/core/actions/post.actions';
import {
  selectPostById,
  selectPostTranslationLoading,
} from 'src/app/core/reducers';

import { AppState } from 'src/app/reducers';
import { FormGroup } from '@angular/forms';
import { FormProperty } from 'src/app/core/models/form.models';
import { FormsService } from 'src/app/core/services/forms.service';
import { NomLanguages } from 'src/app/core/constants';
import { Observable, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-post-translation',
  templateUrl: './post-translation.component.html',
  styleUrls: ['./post-translation.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PostTranslationComponent {
  @Input() postId: string;

  post$: Observable<Post>;
  postTranslationLoading$: Observable<boolean>;

  private destroyRef = inject(DestroyRef);

  post: Post;
  currentLanguage: Language = {
    name: 'English',
    id: NomLanguages.ENGLISH,
    code: 'en',
    rtl: false,
  };
  hasTranslationLanguage: boolean = false;
  hasTranslationLangauge: boolean = false;

  formGroup: FormGroup;
  formProperties: FormProperty[] = [
    {
      propertyName: 'language_picker',
      controlType: 'language-picker',
      additionalConfig: {
        hasDefault: false,
      },
    },
    {
      propertyName: 'title',
      propertyNameLanguages: {
        [NomLanguages.BULGARIAN]:
          'translation.' + NomLanguages.BULGARIAN + '.title',
        [NomLanguages.ENGLISH]:
          'translation.' + NomLanguages.ENGLISH + '.title',
        [NomLanguages.ARABIC]: 'translation.' + NomLanguages.ARABIC + '.title',
      },
      propertyLabel: this.translateService.instant(
        'post-translation.label.translated-title',
      ),
      controlType: 'text',
      additionalConfig: {
        placeholder: this.translateService.instant(
          'post-translation.placeholder.translated-title',
        ),
        disabled: true,
      },
    },
    {
      propertyName: 'translation',
      propertyLabel: this.translateService.instant(
        'post-translation.label.translated-content',
      ),
      controlType: 'textarea',
      propertyNameLanguages: {
        [NomLanguages.BULGARIAN]:
          'translation.' + NomLanguages.BULGARIAN + '.content',
        [NomLanguages.ENGLISH]:
          'translation.' + NomLanguages.ENGLISH + '.content',
        [NomLanguages.ARABIC]:
          'translation.' + NomLanguages.ARABIC + '.content',
      },
      additionalConfig: {
        placeholder: this.translateService.instant(
          'post-translation.placeholder.translated-content',
        ),
        rows: 20,
      },
    },
  ];

  constructor(
    private formService: FormsService,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.post$ = this.store.select(selectPostById(this.postId));
    this.postTranslationLoading$ = this.store.select(
      selectPostTranslationLoading(this.postId),
    );
    this.formGroup = this.formService.createFormGroup(this.formProperties);
    this.post$
      .pipe(
        tap((post) => {
          this.post = post;
          this.formService.populateFormValues(this.formGroup, post);
          this.estimateHasTranslationLanguage();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  estimateHasTranslationLanguage() {
    this.hasTranslationLanguage =
      !!this.post.translation &&
      this.currentLanguage.id in this.post.translation &&
      this.isValidObject(this.post.translation[this.currentLanguage.id]);
    this.cdr.detectChanges();
  }

  estimateTitleProperty(type: string) {
    if (type === 'social') {
      const titleProp = this.formProperties.findIndex(
        (prop) => prop.propertyName === 'title',
      );
      this.formProperties.splice(titleProp, 1);
    }
  }

  isValidObject(obj: any): boolean {
    return obj && Object.keys(obj).length > 0;
  }

  setTranslation() {
    const currentLanguageId = this.currentLanguage.id;
    const title = this.formGroup.get([
      'translation.' + currentLanguageId + '.title',
    ]).value;
    const synopsis = this.formGroup.get([
      'translation.' + currentLanguageId + '.content',
    ]).value;

    // console.log(title);

    this.store.dispatch(
      updatePostTranslation({
        postId: this.post.id,
        title: title ?? '',
        content: synopsis,
        languageId: currentLanguageId,
      }),
    );
  }

  copyFromOriginal() {
    this.formGroup.patchValue({
      ['translation.' + this.currentLanguage.id + '.title']: this.post.title,
      ['translation.' + this.currentLanguage.id + '.content']:
        this.post.content,
    });
  }

  onLanguageChanged(language: Language) {
    this.currentLanguage = language;
    this.estimateHasTranslationLanguage();
  }

  translateContent() {
    // Call the action

    const currentLanguageId = this.currentLanguage.id;
    const title = this.formGroup.get([
      'translation.' + currentLanguageId + '.title',
    ]).value;
    const content = this.formGroup.get([
      'translation.' + currentLanguageId + '.content',
    ]).value;

    this.store.dispatch(
      translatePost({
        postId: this.postId,
        languageId: currentLanguageId,
        title: title ?? '',
        content: content,
      }),
    );
  }
}
